import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import AuthContext from '../../context/authContext';
import useTestBookingCleanup from '../../hooks/useTestBookingCleanup';
import { viewRootTextPrimarySx } from '../../util/chartTheme';

const cleanupRight = 'internalBooking:special:cleanupTestBookings';

const statusLabels = {
  confirmed: 'Bestätigt',
  waitingForPayment: 'Wartet auf Zahlung',
  awaitingConfirmation: 'Wartet auf Bestätigung',
  noShow: 'No-Show',
  blocked: 'Blocker',
};

const formatDateTime = value => (value ? dayjs(value).format('DD.MM.YYYY HH:mm') : '-');

const formatCurrency = value => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '-';
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(
    Number(value)
  );
};

const getCustomerName = booking =>
  `${booking?.firstName || ''} ${booking?.lastName || ''}`.trim() || '-';

const getBranchName = booking =>
  booking?.branch?.longName || booking?.branch?.locationName || booking?.branch?._id || '-';

export default function TestBookingCleanupView() {
  const authCtx = useContext(AuthContext);
  const { isLoading, fetchTestBookings, cancelTestBooking } = useTestBookingCleanup();
  const searchInputRef = useRef(null);
  const [bookings, setBookings] = useState([]);
  const [testEmailAddresses, setTestEmailAddresses] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [total, setTotal] = useState(0);
  const [cancelDialogBooking, setCancelDialogBooking] = useState(null);
  const [cancelingBookingId, setCancelingBookingId] = useState(null);

  const hasCleanupRight = authCtx.checkRightAny(cleanupRight);

  const loadBookings = useCallback(async () => {
    if (!hasCleanupRight) return;
    const result = await fetchTestBookings({
      page: page + 1,
      limit: rowsPerPage,
      searchQuery,
    });
    setBookings(result.bookings || []);
    setTotal(Number(result.pagination?.total || 0));
    setTestEmailAddresses(result.testEmailAddresses || []);
  }, [fetchTestBookings, hasCleanupRight, page, rowsPerPage, searchQuery]);

  useEffect(() => {
    if (!hasCleanupRight) return;
    loadBookings().catch(() => {});
  }, [hasCleanupRight, loadBookings]);

  const handleSearch = () => {
    setPage(0);
    setSearchQuery(String(searchInputRef.current?.value || '').trim());
  };

  const handleSearchKeyDown = event => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  const handleRowsPerPageChange = event => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const closeCancelDialog = () => {
    if (cancelingBookingId) return;
    setCancelDialogBooking(null);
  };

  const handleCancelBooking = async () => {
    if (!cancelDialogBooking?._id) return;

    setCancelingBookingId(cancelDialogBooking._id);
    try {
      await cancelTestBooking(cancelDialogBooking._id);
      toast.success('Testbuchung wurde storniert');
      setCancelDialogBooking(null);
      await loadBookings();
    } catch (_) {
    } finally {
      setCancelingBookingId(null);
    }
  };

  if (!hasCleanupRight) {
    return <Navigate to='/welcome' replace />;
  }

  return (
    <Box sx={{ ...viewRootTextPrimarySx, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent='space-between'>
        <Box>
          <Typography variant='h5'>Testbuchungen aufräumen</Typography>
          <Typography variant='body2' color='text.secondary'>
            Aktive Buchungen mit den konfigurierten Test-E-Mail-Adressen können hier gezielt
            still storniert werden.
          </Typography>
        </Box>
        <Button
          variant='outlined'
          startIcon={<RefreshIcon />}
          onClick={() => loadBookings().catch(() => {})}
          disabled={isLoading}
        >
          Aktualisieren
        </Button>
      </Stack>

      <Alert severity='info'>
        <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap alignItems='center'>
          <Typography variant='body2'>Erkannte Test-Adressen inklusive Plus-Tags:</Typography>
          {testEmailAddresses.map(email => (
            <Chip key={email} size='small' label={`${email.replace('@', '+...@')}`} />
          ))}
        </Stack>
      </Alert>

      <Paper sx={{ p: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
          <TextField
            size='small'
            label='Suche'
            placeholder='Buchungsnummer, Name, E-Mail...'
            inputRef={searchInputRef}
            defaultValue={searchQuery}
            onKeyDown={handleSearchKeyDown}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: { xs: '100%', sm: 320 } }}
          />
          <Button variant='contained' onClick={handleSearch} disabled={isLoading}>
            Suchen
          </Button>
        </Stack>
      </Paper>

      <TableContainer component={Paper}>
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell>Buchung</TableCell>
              <TableCell>Kunde</TableCell>
              <TableCell>E-Mail</TableCell>
              <TableCell>Termin</TableCell>
              <TableCell>Standort</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align='right'>Personen</TableCell>
              <TableCell align='right'>Betrag</TableCell>
              <TableCell align='right'>Aktion</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && bookings.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align='center' sx={{ py: 5 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : bookings.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align='center' sx={{ py: 5 }}>
                  Keine aktiven Testbuchungen gefunden.
                </TableCell>
              </TableRow>
            ) : (
              bookings.map(booking => (
                <TableRow key={booking._id} hover>
                  <TableCell>
                    <Typography variant='body2' fontWeight={700}>
                      {booking.bookingNumber || '-'}
                    </Typography>
                    <Typography variant='caption' color='text.secondary'>
                      {booking.entertainment || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>{getCustomerName(booking)}</TableCell>
                  <TableCell>{booking.mail || '-'}</TableCell>
                  <TableCell>{formatDateTime(booking.startTime)}</TableCell>
                  <TableCell>{getBranchName(booking)}</TableCell>
                  <TableCell>
                    <Chip
                      size='small'
                      label={statusLabels[booking.status] || booking.status || '-'}
                      color={booking.status === 'waitingForPayment' ? 'warning' : 'default'}
                    />
                  </TableCell>
                  <TableCell align='right'>{booking.participantCount ?? '-'}</TableCell>
                  <TableCell align='right'>{formatCurrency(booking.totalAmount)}</TableCell>
                  <TableCell align='right'>
                    <Tooltip title='Testbuchung stornieren'>
                      <span>
                        <IconButton
                          size='small'
                          color='error'
                          onClick={() => setCancelDialogBooking(booking)}
                          disabled={Boolean(cancelingBookingId)}
                        >
                          <CancelIcon fontSize='small' />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component='div'
          count={total}
          page={page}
          onPageChange={(_, nextPage) => setPage(nextPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage='Zeilen pro Seite'
        />
      </TableContainer>

      <Dialog open={Boolean(cancelDialogBooking)} onClose={closeCancelDialog} maxWidth='sm' fullWidth>
        <DialogTitle>Testbuchung stornieren?</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5}>
            <Typography>
              Die Buchung {cancelDialogBooking?.bookingNumber || ''} von{' '}
              {getCustomerName(cancelDialogBooking)} wird still storniert.
            </Typography>
            <Alert severity='warning'>
              Es werden keine Kunden- oder Arena-Benachrichtigungen versendet. Diese Aktion ist für
              Testbuchungen mit den hinterlegten E-Mail-Adressen vorgesehen.
            </Alert>
            <Box>
              <Typography variant='body2'>
                <strong>E-Mail:</strong> {cancelDialogBooking?.mail || '-'}
              </Typography>
              <Typography variant='body2'>
                <strong>Termin:</strong> {formatDateTime(cancelDialogBooking?.startTime)}
              </Typography>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCancelDialog} disabled={Boolean(cancelingBookingId)}>
            Abbrechen
          </Button>
          <Button
            color='error'
            variant='contained'
            onClick={handleCancelBooking}
            disabled={Boolean(cancelingBookingId)}
            startIcon={
              cancelingBookingId ? <CircularProgress color='inherit' size={16} /> : <CancelIcon />
            }
          >
            Stornieren
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
